
import { isAppUsing } from 'bits/flip'
import { navigateToMap } from 'bits/location'
import { anonymous, blankUser, isRegistered } from 'bits/user_model'
import { Component, Getter, namespace, Prop, Vue, Watch } from 'bits/vue'
import OzAvatar from 'library/v4/components/OzAvatar.vue'
import OzDivider, { OzDividerColorScheme } from 'library/v4/components/OzDivider.vue'
import OzDrawerDraggable from 'library/v4/components/OzDrawerDraggable.vue'
import OzIcon from 'library/v4/components/OzIcon.vue'
import RelativeTime from 'library/v4/components/relative-time.vue'
import type { Id, Post, Section, User, WallReactionData } from 'types'
import SurfacePanelPostExpandedTextPanelComments from 'vuecomponents/SurfacePanelPostExpandedTextPanelComments.vue'
import SurfacePostBody from 'vuecomponents/SurfacePostBody.vue'
import SurfacePostSocialContentTopBar from 'vuecomponents/SurfacePostSocialContentTopBar.vue'

@Component({
  components: {
    OzAvatar,
    OzDivider,
    OzDrawerDraggable,
    OzIcon,
    RelativeTime,
    SurfacePanelPostExpandedTextPanelComments,
    SurfacePostBody,
    SurfacePostSocialContentTopBar,
  },
})
export default class SurfacePanelPostExpandedTextPanelMobile extends Vue {
  $refs!: {
    author: HTMLDivElement
    postSubject: HTMLHeadingElement
    bodyWrapper: HTMLDivElement
    divider: OzDivider
    socialContentTopBar: SurfacePostSocialContentTopBar & {
      $el: HTMLDivElement
    }
    commentWrapper: SurfacePanelPostExpandedTextPanelComments & {
      $el: HTMLDivElement
    }
  }

  @Prop({ default: () => {} }) reactionIdsByPostId!: Record<Id, Id[]>
  @Prop({ default: () => {} }) commentIdsByPostId!: Record<Id, Id[]>

  @Getter isSectionable!: boolean

  @namespace('browser').Getter isSmallerThanTabletPortrait!: boolean
  @namespace('browser').Getter windowHeight!: number
  @namespace('browser').State darkMode!: true | false | 'auto'

  OzDividerColorScheme = OzDividerColorScheme

  get xDivider(): boolean {
    // don't show divider if the text panel is completely blank
    return this.xAuthor || !!this.post.subject || !!this.postBody || !!this.post.location_name || !!this.section
  }

  /**
   * POST
   */
  @Getter xAuthor!: boolean
  @namespace('postAction').Getter expandedPost!: Post & { id: Id }
  @namespace('section').Getter sectionById!: (id: Id) => Section | null
  @namespace('userContributor').Getter getUserById!: (id: Id) => User | null

  get post(): Post & { id: Id } {
    return this.expandedPost
  }

  get section(): Section | null {
    if (!this.isSectionable) return null
    return this.post.wall_section_id ? this.sectionById(this.post.wall_section_id) : null
  }

  get author(): Required<User> {
    const foundUser = this.post.author_id ? this.getUserById(this.post.author_id) : null
    return (foundUser || blankUser()) as Required<User>
  }

  get authorName(): string {
    return this.isAuthorRegistered ? this.author.name || this.author.username : anonymous
  }

  get isAuthorRegistered(): boolean {
    return isRegistered(this.author)
  }

  get postBody(): string | undefined {
    return this.post.body
  }

  openInMap(): void {
    if (!this.post.location_point) return
    navigateToMap({
      lat: this.post.location_point.latitude,
      lng: this.post.location_point.longitude,
      name: this.post.location_name || '',
    })
  }

  /**
   * REACTIONS
   */
  @Getter isPhone!: boolean
  @Getter isReactable!: boolean
  @Getter reactionData!: WallReactionData
  @Getter user!: User
  @namespace('reaction').State postIdBeingReactedTo!: Id
  @namespace('reaction').Action startEditingReaction!: (payload: {
    postId: Id
    dropdownLeft?: number
    dropdownWidth?: number
    dropdownTop?: number
  }) => void

  get xReactions(): boolean {
    // when a user is viewing a wall with reaction disabled and then reaction is enabled + someone react to something
    // the realtime message for new reaction may come before the reaction enable message
    // => wait for reactionData to be available before showing reactions
    return (this.isReactable || this.reactionIdsByPostId[this.post.id]?.length > 0) && !!this.reactionData
  }

  get isBeingReacted(): boolean {
    return this.postIdBeingReactedTo === this.post.id
  }

  editReaction(): void {
    if (this.isPhone) {
      this.startEditingReaction({ postId: this.post.id })
      return
    }
    if (this.$el.closest('.surface-post')?.classList.contains('noclick')) {
      // Prevent reaction event when the post is being dragged
      return
    }

    const { left, top, width } = this.$refs.socialContentTopBar.$el.getBoundingClientRect()

    this.startEditingReaction({
      postId: this.post.id,
      dropdownLeft: left,
      dropdownTop: top,
      dropdownWidth: width,
    })
  }

  /**
   * COMMENTS
   */

  @Getter isCommentable!: boolean

  get xComments(): boolean {
    if (!isAppUsing('expandedPostComments')) return false
    return this.post.published === true && (this.isCommentable || this.commentCount > 0)
  }

  get commentCount(): number {
    return this.post.id ? this.commentIdsByPostId[this.post.id]?.length || 0 : 0
  }

  /**
   * DRAGGING LOGIC
   */

  layout: 'expanded' | 'collapsed' = 'collapsed'
  preferredTrayStartHeight = 0
  isDragging = false
  dragDistance = 0
  maxBodyHeight = Infinity
  maxCommentHeight = Infinity

  get xExpandedLayout(): boolean {
    return this.layout === 'expanded'
  }

  get isDraggingUp(): boolean {
    return this.isDragging && this.dragDistance < 0
  }

  get bodyDragHeightString(): string {
    if (!this.xExpandedLayout && !this.isDraggingUp) {
      return '0px'
    }

    // Increase the height of body and comment element as dragging is happening.
    // We increase each height by half of the drag distance so the combined increment is
    // the whole drag distance. Therefore the height of the text panel is increased by the
    // same distance the finger travelled on the screen.
    // When body or comment is already expanded to max, we start to increase the other
    // height by the whole drag distance so that the total height still increase consistently.

    const absDragDistance = Math.abs(this.dragDistance)
    const computedBodyDragDistance =
      absDragDistance / 2 <= this.maxCommentHeight
        ? absDragDistance / 2
        : this.maxCommentHeight + (absDragDistance - this.maxCommentHeight * 2)

    return this.isDragging && this.dragDistance < 0
      ? `${Math.min(Math.abs(computedBodyDragDistance), this.maxBodyHeight)}px`
      : `auto`
  }

  get commentDragHeightString(): string {
    if (!this.xExpandedLayout && !this.isDraggingUp) {
      return '0px'
    }
    const absDragDistance = Math.abs(this.dragDistance)
    const computedCommentDragDistance =
      absDragDistance / 2 <= this.maxBodyHeight
        ? absDragDistance / 2
        : this.maxBodyHeight + (absDragDistance - this.maxBodyHeight * 2)
    return this.isDragging && this.dragDistance < 0
      ? `${Math.min(Math.abs(computedCommentDragDistance), this.maxCommentHeight)}px`
      : `auto`
  }

  onDragStart(): void {
    this.isDragging = true
  }

  onDragging(distance: number): void {
    this.dragDistance = distance
  }

  onDragEnd(distance: number): void {
    this.isDragging = false
    this.dragDistance = 0
    if (distance < 0) {
      // dragged up
      this.layout = 'expanded'
    } else {
      // dragged down
      this.layout = 'collapsed'
    }
  }

  onSwipedown({ isAtTopBeforeDragging }: { isAtTopBeforeDragging: boolean }): void {
    if (!isAtTopBeforeDragging) {
      this.$emit('close')
    }
  }

  calculateTrayHeight(): void {
    this.preferredTrayStartHeight =
      16 + // drawer handle height
      12 + // top margin for the whole text panel
      (this.$refs.author ? this.$refs.author.scrollHeight + 10 : 0) + // subject
      (this.$refs.postSubject ? this.$refs.postSubject.scrollHeight : 0) + // subject
      (this.$refs.divider ? this.$refs.divider.$el.scrollHeight + 24 : 0) + // divider and divider top margin
      (this.$refs.socialContentTopBar?.$el ? this.$refs.socialContentTopBar.$el.scrollHeight : 0) +
      (this.$refs.divider ? 32 : 16) // socialContentTopBar's margin
  }

  calculateMaxHeightForDraggableComponents(): void {
    this.$nextTick(() => {
      this.maxBodyHeight = this.$refs.bodyWrapper?.scrollHeight || Infinity
      this.maxCommentHeight = this.$refs.commentWrapper?.$el.scrollHeight || Infinity
    })
  }

  /**
   * WATCHERS
   */

  @Watch('expandedPost')
  onExpandedPostChange(): void {
    this.$nextTick(() => {
      this.calculateTrayHeight()
    })
    this.calculateMaxHeightForDraggableComponents()
  }

  /**
   * LIFECYCLE
   */

  mounted(): void {
    this.calculateTrayHeight()
    this.calculateMaxHeightForDraggableComponents()
  }
}
